import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import {
  AfterViewInit, Component, ContentChildren, ElementRef, HostListener,
  Input, QueryList, ViewChild, ViewChildren, OnDestroy, OnInit
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { circular } from 'axis-components/components/carousel/circular';
import { CarouselItemDirective } from 'axis-components/components/carousel/carousel-item.directive';
import { CarouselItemElement } from 'axis-components/components/carousel/carousel-element.directive';

@Component({
  selector: 'axis-carousel',
  exportAs: 'axis-carousel',
  templateUrl: './carousel.component.html'
})
export class CarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  @ContentChildren(CarouselItemDirective) items: QueryList<CarouselItemDirective>;
  @ViewChildren(CarouselItemElement, { read: ElementRef }) private itemsElements: QueryList<ElementRef>;
  @ViewChild('carousel', { static: true }) private carousel: ElementRef;
  @Input() timing = '750ms 50ms ease-in-out';
  @Input() showControls = true;
  @Input() autoScroll: number;
  @Input() ucid = '';
  @Input() itemsCount = 1;
  @Input() customClass = '';
  private timerCheck = false;
  private player: AnimationPlayer;
  private itemWidth: number;
  private carouselWidth: number;
  public currentSlide = 0;
  public activeIndicator = 0;
  private interval: Subscription;
  public arrSliders: number[];
  public carouselWrapperStyle = {};
  public itemStyle = {};
  public circular: any = null;
  public startingSlide;
  public endingSlide;
  public thresholdLimit = 50;
  constructor(private builder: AnimationBuilder) { }

  ngOnInit(): void {
    this.ucid = this.ucid + '-' + (Math.random() + Math.random()).toString().replace('.', '');
  }

  private buildAnimation(offset, time: any) {
    return this.builder.build([
      animate(time == null ? this.timing : 0,
        style({ transform: `translateX(-${offset}px)` }))
    ]);
  }
  // Progresses the carousel forward by 1 slide.
  next() {
    this.circular.next();
    this.activeIndicator = this.circular.current();
    // if we are in the last
    if (this.currentSlide + this.itemsCount === this.items.length) {
      // reorder the QueryList,
      let arr = this.items.toArray();
      const first = arr.shift();  // remove the first element
      arr = arr.concat([first]);  // Concat at last of the array
      this.items.reset(arr);
      this.currentSlide--;
      this.transitionCarousel(0);  // execute the animation in 0 seconds
    }
    this.currentSlide = (this.currentSlide + 1) % this.items.length;
    this.transitionCarousel(null);
  }

  // Regresses the carousel backwards by 1 slide.
  prev() {
    this.circular.prev();
    this.activeIndicator = this.circular.current();
    if (this.currentSlide === 0) {
      let arr = this.items.toArray();
      const last = arr.pop();  // remove the last element
      arr = [last].concat(arr); // create an array with the last element+arr
      this.items.reset(arr);
      this.currentSlide++;  // change the currentSlide
      this.transitionCarousel(0);
    }
    this.currentSlide = (this.currentSlide - 1 + this.items.length) % this.items.length;
    this.transitionCarousel(null);
  }

  ngAfterViewInit() {
    this.reSizeCarousel();
    setTimeout(() => { // settimeout is added to carousel window resize issue
      this.setItemWidth();
    });
    this.arrSliders = Array.from(Array(this.items.length)).map((e, i) => i + 1);
    this.circular = new circular(this.arrSliders, 0);
    this.activeIndicator = this.circular.current();
    this.autoCarousel();
  }

  selectedIndicator(selectedIndex: number) {
    let pos = selectedIndex - this.circular.current();
    while (pos > 0) {
      this.next();
      pos--;
    }
    while (pos < 0) {
      this.prev();
      pos++;
    }
 }

  private autoCarousel() {
    if (this.timerCheck) {
      this.interval.unsubscribe();
      this.interval = interval(+this.autoScroll).subscribe(time => {
        this.next();
      });
    }
    if (this.autoScroll && !this.timerCheck) {
      this.timerCheck = true;
      this.interval = interval(+this.autoScroll).subscribe(time => {
        this.next();
      });
    }
  }
  
  // touch and mouse events are added to carousel
  @HostListener('touchstart', ['$event'])
  @HostListener('mousedown', ['$event'])
  onStart(event: any) {
    if (event.cancelable) {
      event.preventDefault();
    }
    if (event.type === 'touchstart') {
      this.startingSlide = event.changedTouches[0].clientX;
    } else if (event.type === 'mousedown') {
      event.preventDefault();
      this.startingSlide = event.clientX;
    }
  }

  @HostListener('touchend', ['$event'])
  @HostListener('mouseup', ['$event'])
  onEnd(event: any) {
    if (event.cancelable) {
      event.preventDefault();
    }
    if (event.type === 'touchend') {
      this.endingSlide = event.changedTouches[0].clientX;
    } else if (event.type === 'mouseup') {
      if (event.target.innerText === 'chevron_left' || event.target.innerText === 'chevron_right') {
        return;
      }
      this.endingSlide = event.clientX;
    }

    if ((this.endingSlide > this.startingSlide + this.thresholdLimit) || (event.target.innerText === 'chevron_left')) {
      this.prev();
    } else if ((this.endingSlide < this.startingSlide - this.thresholdLimit) || (event.target.innerText === 'chevron_right')) {
      this.next();
    }
  }

  // Listens for changes to the viewport size and triggers a re-sizing of the carousel.
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    event.preventDefault();
    this.reSizeCarousel();
    setTimeout(() => {
      this.setItemWidth();
    });
  }

  // Re-sizes the carousel container and triggers `this.transitionCarousel()` to reset the childrens' positions.
  // For use on initial load, and when changing viewport size.
  reSizeCarousel(): void {
    // re-size the container
    this.carouselWidth = this.itemsElements.first.nativeElement.parentElement.clientWidth;
    if (this.carouselWidth) {
      this.carouselWrapperStyle = {
        width: `${this.carouselWidth}px`,
      };
    }
    if (this.itemsCount > 1) {
      if (this.itemsCount > this.items.length) {
        this.itemsCount = this.items.length;
      }
      this.itemWidth = this.carouselWidth / this.itemsCount;
      if (this.itemWidth) {
        this.itemStyle = {
          width: `${this.itemWidth}px`,
        };
      }
    }
    // trigger a fresh transition to the current slide to reset the position of the children
    // this.transitionCarousel(null);
  }

  setItemWidth() {
    this.itemWidth = this.itemsElements.first.nativeElement.children[0].firstChild.clientWidth;
    this.transitionCarousel(null);
  }

  // Animates the carousel to the currently selected slide.
  transitionCarousel(time: any) {
    const offset = this.currentSlide * this.itemWidth;
    const myAnimation: AnimationFactory = this.buildAnimation(offset, time);
    this.player = myAnimation.create(this.carousel.nativeElement);
    this.player.play();
  }

  ngOnDestroy() { }
}
